import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolSpineAnimation } from '@/containers/symbols/SymbolSpineAnimation'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import { SymbolOutOverlayAnimationPool, SymbolsAnimationPool } from '@/factories/Factory'
import { IPoolable } from '@base/pool/IPoolable'
import gsap from 'gsap'
import type { TrackEntry as ITrackEntry } from '@esotericsoftware/spine-pixi-v8'
import * as PIXI from 'pixi.js'
import { IAnimationWrapper, NewSymbolData, SymbolContainerData } from '@/modules'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { COIN_VALUE_STYLE } from '@/containers/common/TextStyles'
import { formatCoinDisplayValue } from '@/utils/FormatCoinDisplayValue'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import GameSignals from '@/signals/GameSignals'

export class SymbolContainer extends PIXI.Container implements IPoolable {
    private static SHADOW_IN_DEFAULT_TINT_POWER: number
    private static readonly BONUS_SYMBOL_EXTRA_SCALE: number = 1.05
    // Backup switch: set to false to instantly restore legacy behavior
    private static readonly USE_DYNAMIC_SYMBOL_ANIMATION_FIT: boolean = true
    // Legacy scale kept as backup/reference
    private static readonly LEGACY_SYMBOL_ANIMATION_SCALE: number = 1.2
    private static readonly HIGH_PAY_SYMBOL_SCALE_FALLBACK: number = 0.8
    private static readonly DEBUG_OVERLAY_STATIC_FOR_HIGH_PAY: boolean = false
    private static readonly AVAILABLE_MULTIPLIER_TEXTURES: number[] = [2, 3, 4, 5, 6, 8, 10, 15, 20]
    private static readonly SYMBOL_OUT_TARGET_TIMESCALE: number = 19 / 14
    private static readonly SYMBOL_OUT_START_OFFSET_SECONDS: number = 0.08
    private static readonly BONUS_SYMBOL_STATIC_SCALE_CORRECTION: number = 1.05
    private static readonly WILD_SYMBOL_STATIC_SCALE_CORRECTION: number = 1.28
    private static readonly COIN_SYMBOL_STATIC_SCALE_CORRECTION: number = 0.88
    
    //## 0.7 means 70%
    private static readonly COIN_VALUE_BLUR_AMOUNT: number = 0.7
    private static readonly COIN_VALUE_BLUR_STRENGTH_Y: number = 8 * SymbolContainer.COIN_VALUE_BLUR_AMOUNT
    
    //## Golden coin value [_animationMultiplierValue] needs to be offset slightly down to sit better on the coin.
    private static readonly COIN_VALUE_X_OFFSET: number = -4
    private static readonly COIN_VALUE_Y_OFFSET: number = 4
    
    private static readonly BONUS_SYMBOL_ANIMATION_SCALE_BOOST: number = 1.01
    private _symbolData: SymbolContainerData
    private _tintPower: number
    private readonly _staticImage: PIXI.Sprite
    private readonly _animationWrapper: PIXI.Container
    private rect: PIXI.Graphics
    private _animation: SymbolSpineAnimation
    private _animationCompleteCallback: () => void
    private _animationTrack: ITrackEntry
    private readonly _multiplierValue: ScalingTextField
    private readonly _multiplierValueImage: PIXI.Sprite
    private readonly _animationMultiplierValue: ScalingTextField
    private readonly _multiplierBlurFilter: PIXI.BlurFilter
    private _hpOutOverlayWrapper: PIXI.Container | null
    private _fixedAnimationScale: number | null
    private _staticTextureName: string

    public constructor() {
        super()
        SymbolContainer.SHADOW_IN_DEFAULT_TINT_POWER = Facade.symbolsConfig.SHADOW_OPACITY

        this._staticImage = new PIXI.Sprite()
        this._staticImage.anchor.set(0.5)
        this._staticImage.interactive = false

        this.addChild(this._staticImage)
        // const rect = new PIXI.Graphics()
        // // rect.x = -58
        // // rect.y = -53
        // rect.beginFill(0, 0.001)
        // rect.lineStyle(1, 0xff0000)
        // rect.drawRect(5, 5, 100, 100)

        // rect.interactive = true
        // rect.buttonMode = true
        // this.addChild(rect)

        this._animationWrapper = new PIXI.Container()
        this._animationWrapper.visible = false
        this.addChild(this._animationWrapper)

        this._multiplierValue = new ScalingTextField(0.65, 190, PIXI.BitmapText, COIN_VALUE_STYLE)
        this._multiplierValue.text = ''
        this._multiplierValue.position.set(0, 0)
        this._multiplierValue.setCentered(true)
        this.addChild(this._multiplierValue)

        this._multiplierValueImage = new PIXI.Sprite()
        this._multiplierValueImage.anchor.set(0.5)
        this._multiplierValueImage.position.set(0, 0)
        this._multiplierValueImage.visible = false
        this.addChild(this._multiplierValueImage)

        this._animationMultiplierValue = new ScalingTextField(0.65, 190, PIXI.BitmapText, COIN_VALUE_STYLE)
        this._animationMultiplierValue.text = ''
        this._animationMultiplierValue.scale.set(1)
        this._animationMultiplierValue.position.set(0, 0)
        this._animationMultiplierValue.setCentered(true)
        // Light vertical blur only — coin art already uses a blur sprite; heavy text blur
        // made prize values unreadable while reels were spinning.
        this._multiplierBlurFilter = new PIXI.BlurFilter({
            strengthX: 0,
            strengthY: SymbolContainer.COIN_VALUE_BLUR_STRENGTH_Y,
            quality: 2,
        })
        this._hpOutOverlayWrapper = null
        this._fixedAnimationScale = null
        this._staticTextureName = ''

        this._symbolData = null
        this._tintPower = 0
        // this.rect = new PIXI.Graphics()
        // this.rect.x = -58
        // this.rect.y = -53
        // this.rect.beginFill(0, 0.001)
        // this.rect.lineStyle(1, 0xff0000)
        // this.rect.drawRect(5, 5, 200, 200)
        // this.rect.interactive = true
        // this.rect.buttonMode = true
        // this.addChild(this.rect)
    }

    public init(data: NewSymbolData) {
        this.reset()
        this._symbolData = data.symbolData

        const resolveTextureName = (): string => {
            const indexes = Facade.symbolsConfig?.indexes || {}

            const requestedIndex = Number(this._symbolData?.index)

            if (requestedIndex === SpecialSymbolType.MULTIPLIER) {
                const multiplierStaticTexture = this.resolveMultiplierStaticTextureName(String(this._symbolData?.coinType || ''))
                if (multiplierStaticTexture) {
                    return multiplierStaticTexture
                }
            }

            const resolvedIndex = requestedIndex

            const candidate = indexes?.[resolvedIndex]

            if (typeof candidate === 'string') {
                return candidate
            }

            if (candidate && typeof candidate === 'object') {
                const candidateObj = candidate as Record<string, unknown>
                const objectFallback =
                    candidateObj.texture ||
                    candidateObj.static ||
                    candidateObj.idle ||
                    candidateObj.normal ||
                    candidateObj.default

                if (typeof objectFallback === 'string') {
                    return objectFallback
                }
            }

            const safeFallback = indexes?.[SpecialSymbolType.MULTIPLIER]
            return typeof safeFallback === 'string' ? safeFallback : ''
        }

        const imageTextureName: string = resolveTextureName()
        this._staticTextureName = imageTextureName
        this._staticImage.texture = PIXI.Texture.from(imageTextureName)
        this.setImageScale(this._symbolData.index)
        this._staticImage.name = 'STATIC'
        this._multiplierValue.visible = false
        this._multiplierValue.text = ''

        const rawMultiplier = Number(data.symbolData.multiplier)
        const rawCoinValue = Number(data.symbolData.coinValue)
        const coinType = String(data.symbolData.coinType || '').toLowerCase()
        const isJackpotCoin = this.isJackpotCoinType(coinType)
        const displayValue = Number.isFinite(rawCoinValue) && rawCoinValue > 0 ? rawCoinValue : rawMultiplier
        // Show value on any coin (MULTIPLIER) that has a positive value, not just prize ones
        // Jackpot coin types (grand/major/minor/mini) should not show numeric value over the coin.
        const hasDisplayValue =
            this.index === SpecialSymbolType.MULTIPLIER && Number.isFinite(displayValue) && displayValue > 0 && !isJackpotCoin
        const displayLabel = hasDisplayValue ? this.formatCoinDisplayValue(displayValue) : ''
        this._animationMultiplierValue.text = displayLabel

        if (this.index === SpecialSymbolType.MULTIPLIER) {
            this._multiplierValueImage.visible = false
            this._multiplierValueImage.texture = PIXI.Texture.EMPTY
            this._multiplierValueImage.scale.set(1)

            this._multiplierValue.visible = hasDisplayValue
            this._multiplierValue.text = displayLabel

            //## Apply the golden coin value offset.
            this._multiplierValue.position.set(SymbolContainer.COIN_VALUE_X_OFFSET, SymbolContainer.COIN_VALUE_Y_OFFSET)
            
            // Keep amount tint in sync with current shadow/anticipation dimming.
            this.tintPower = this._tintPower
        } else {
            this._multiplierValueImage.visible = false
            this._multiplierValueImage.texture = PIXI.Texture.EMPTY
            this._multiplierValueImage.scale.set(1)
            this._multiplierValue.visible = false
            this._multiplierValue.text = ''
        }

        this.detachAnimation()
    }

    private resolveMultiplierStaticTextureName(coinTypeRaw: string): string | null {
        const coinType = String(coinTypeRaw || '').toLowerCase()

        if (!coinType) {
            return null
        }

        if (coinType.includes('grand') || coinType.includes('jackpot')) {
            return 'symbol-static_coin_grand.png'
        }

        if (coinType.includes('major')) {
            return 'symbol-static_coin_major.png'
        }

        if (coinType.includes('minor')) {
            return 'symbol-static_coin_minor.png'
        }

        if (coinType.includes('mini')) {
            return 'symbol-static_coin_mini.png'
        }

        if (coinType.includes('prize') || coinType.includes('gold')) {
            return 'symbol-static_coin.png'
        }

        return null
    }

    private isJackpotCoinType(coinType: string): boolean {
        const normalized = String(coinType || '').toLowerCase()

        return (
            normalized.includes('grand') ||
            normalized.includes('major') ||
            normalized.includes('minor') ||
            normalized.includes('mini')
        )
    }

    private formatCoinDisplayValue(value: number): string {
        return formatCoinDisplayValue(value)
    }

    protected onClick(rect: any, staticImg: any) {
        if (rect.parent.name === String(SpecialSymbolType.SCATTER)) {
            GameSignals.showBigDescripion.emit(2221)
        } else {
            GameSignals.showSmallDescripion.emit(2221)
        }
        //staticImg.visible = !staticImg.visible

        // image.scale.set(0.5)
    }
    protected setImageScale = (symbolId: number) => {
        // this.rect.interactive = true
        // this.rect.buttonMode = true

        this._staticImage.anchor.set(0.5)

        const texture = this._staticImage.texture
        const textureWidth = texture?.orig?.width ?? texture?.width ?? 0
        const textureHeight = texture?.orig?.height ?? texture?.height ?? 0
        const textureScaleCorrection = this.getStaticTextureScaleCorrection()

        if (textureWidth > 0 && textureHeight > 0) {
            const targetWidth = Facade.symbolsConfig.WIDTH
            const targetHeight = Facade.symbolsConfig.HEIGHT
            const fitScale = Math.min(targetWidth / textureWidth, targetHeight / textureHeight) * 0.99
            const staticMultiplierScaleBoost = this.getStaticSymbolScaleBoost(symbolId)
            this._staticImage.scale.set(
                symbolId === SpecialSymbolType.SCATTER
                    ? fitScale * SymbolContainer.BONUS_SYMBOL_EXTRA_SCALE * textureScaleCorrection
                    : fitScale * staticMultiplierScaleBoost * textureScaleCorrection
            )
        } else {
            this._staticImage.scale.set(
                symbolId === SpecialSymbolType.SCATTER
                    ? SymbolContainer.BONUS_SYMBOL_EXTRA_SCALE * textureScaleCorrection
                    : symbolId === SpecialSymbolType.MULTIPLIER
                      ? 1.18 * textureScaleCorrection
                      : this.getStaticSymbolScaleBoost(symbolId) * textureScaleCorrection
            )
        }
        //this.rect.on('pointerdown', this.onClick.bind(this, this.rect, this._staticImage))
    }

    private getStaticSymbolScaleBoost(symbolId: number): number {
        if (symbolId === SymbolType.LP_5) {
            return 1.08
        }

        if (symbolId === SpecialSymbolType.SCATTER) {
            return 1.1
        }

        return 1
    }

    private getStaticTextureScaleCorrection(): number {
        const textureName = String(this._staticTextureName || '').toLowerCase()

        if (textureName.includes('bonus')) {
            return SymbolContainer.BONUS_SYMBOL_STATIC_SCALE_CORRECTION
        }

        if (textureName.includes('wild')) {
            return SymbolContainer.WILD_SYMBOL_STATIC_SCALE_CORRECTION
        }

        if (textureName.includes('coin')) {
            return SymbolContainer.COIN_SYMBOL_STATIC_SCALE_CORRECTION
        }

        return 1
    }

    public reset() {
        gsap.killTweensOf(this, 'tintPower')
        this.detachAnimation()
        this.clearHpOutOverlayAnimation()

        this._symbolData = null
        this._staticImage.tint = 0xffffff
        this._multiplierValue.content.tint = 0xffffff
        this._multiplierValueImage.tint = 0xffffff
        this._tintPower = 0

        this.filters = null
        this.visible = true

        this._multiplierValue.text = ''
        this._multiplierValue.visible = false
        
        //## Reset the golden coin value..!
        this._multiplierValue.position.set(0, 0)

        this._multiplierValueImage.visible = false
        this._multiplierValueImage.texture = PIXI.Texture.EMPTY
        this._multiplierValueImage.scale.set(1)
        this._animationMultiplierValue.text = ''
        this._animationMultiplierValue.scale.set(1)
        this.scale.set(1)
        this._animationMultiplierValue.maxWidth = 190
        this._fixedAnimationScale = null
        this._staticTextureName = ''
    }

    private getBlurTextureName(): string {
        if (!this._staticTextureName) {
            return ''
        }

        if (this._staticTextureName.startsWith('symbol-static_')) {
            return this._staticTextureName.replace('symbol-static_', 'symbol-blur_')
        }

        return ''
    }

    public enableBlurStaticTexture() {

        if (this.index === SpecialSymbolType.MULTIPLIER) {
            this._multiplierValue.filters = [this._multiplierBlurFilter]
            this._animationMultiplierValue.filters = [this._multiplierBlurFilter]
        }

        const blurTextureName = this.getBlurTextureName()

        if (!blurTextureName) {
            return
        }

        const blurTexture = PIXI.Texture.from(blurTextureName)

        if (!blurTexture || blurTexture === PIXI.Texture.EMPTY) {
            return
        }

        const currentScaleX = this._staticImage.scale.x
        const currentScaleY = this._staticImage.scale.y

        this._staticImage.texture = blurTexture
        this._staticImage.scale.set(currentScaleX, currentScaleY)
    }

    public disableBlurStaticTexture() {
        this._multiplierValue.filters = null
        this._animationMultiplierValue.filters = null

        if (!this._staticTextureName) {
            return
        }

        const currentScaleX = this._staticImage.scale.x
        const currentScaleY = this._staticImage.scale.y

        this._staticImage.texture = PIXI.Texture.from(this._staticTextureName)

        if (currentScaleX !== 0 && currentScaleY !== 0) {
            this._staticImage.scale.set(currentScaleX, currentScaleY)
            return
        }

        this.setImageScale(this._symbolData.index)
    }

    public playHpOutOverlayAnimation = (loop: boolean = false) => {
        this.clearHpOutOverlayAnimation()

        const wrapper: PIXI.Container = new PIXI.Container() as IAnimationWrapper
        wrapper.position.set(0, 0)
        this.addChild(wrapper)
        this._hpOutOverlayWrapper = wrapper

        const animation: SpineAnimation = SymbolOutOverlayAnimationPool.out(wrapper, { name: 'symbol_out' })
        if (!animation) {
            console.warn('[HP_OUT_OVERLAY] animation instance not available', { index: this._symbolData?.index })
            this.clearHpOutOverlayAnimation()
            return
        }
        ;(wrapper as IAnimationWrapper).animation = animation
        animation.scale.set(1)

        try {
            animation.start(
                ['animation'],
                [loop],
                0,
                [SymbolContainer.SYMBOL_OUT_TARGET_TIMESCALE],
                () => {
                    if (!loop) {
                        this.clearHpOutOverlayAnimation()
                    }
                },
                null,
                SymbolContainer.SYMBOL_OUT_START_OFFSET_SECONDS
            )
        } catch (error) {
            console.warn('[HP_OUT_OVERLAY] failed to start', {
                index: this._symbolData?.index,
                error,
            })
            this.clearHpOutOverlayAnimation()
        }
    }

    public stopHpOutOverlayAnimation = () => {
        this.clearHpOutOverlayAnimation()
    }

    private clearHpOutOverlayAnimation = () => {
        if (!this._hpOutOverlayWrapper) {
            return
        }

        const wrapper = this._hpOutOverlayWrapper as IAnimationWrapper
        const animation = wrapper.animation as SpineAnimation

        if (animation) {
            animation.stop()
            wrapper.removeChild(animation)
            SymbolOutOverlayAnimationPool.in(animation)
        }

        if (wrapper.parent) {
            wrapper.parent.removeChild(wrapper)
        }

        this._hpOutOverlayWrapper = null
    }

    public playAnimation(
        state: string | string[],
        loop: boolean | boolean[] = false,
        callback: () => void = null,
        allowMultiplierLanding: boolean = false
    ) {
        const states: string[] = Array.isArray(state) ? state : [state]
        const hasLandingState = states.includes('land')
        const isDebugHighSymbol = this.index === SymbolType.HP_1 || this.index === SymbolType.HP_2

        // Safety: multiplier symbol should stay static during generic reel landing.
        if (this.index === SpecialSymbolType.MULTIPLIER && hasLandingState && !allowMultiplierLanding) {
            if (callback) {
                callback()
            }
            return
        }

        this.attachAnimation()

        if (this._animation != null) {
            const loops: boolean[] = Array.isArray(loop) ? loop : [loop]

            if (this.shouldIgnoreRedundantLandIdleChain(states, loops)) {
                if (callback) {
                    callback()
                }
                return
            }

            if (this.shouldIgnoreRedundantIdleLoop(states, loops)) {
                this.pauseIdleIfFsPopupActive()
                if (callback) {
                    callback()
                }
                return
            }

            if (this.shouldQueueIdleAfterLand(states, loops)) {
                this._animationCompleteCallback = null
                this.removeAnimationTrack()

                const queuedTrack = this._animation.queue(states[0], loops[0] ?? false)
                this._animationTrack = queuedTrack
                this.setAnimationPosition()

                if (this._animationTrack == null) {
                    this.detachAnimation()
                    if (callback) {
                        callback()
                    }
                    return
                }

                this.revealPreparedAnimationIfReady()
                this._animationCompleteCallback = callback
                this._animationTrack.listener = {
                    start: () => this.pauseIdleIfFsPopupActive(),
                    complete: this.onCompleteAnimation,
                    interrupt: this.onInterruptAnimation,
                    end: this.onEndAnimation,
                }
                this.pauseIdleIfFsPopupActive()
                return
            }

            // High-pay win↔idle: mix without stop()/setToSetupPose (avoids line-by-line jerk).
            if (this.tryPlayHighPayWinIdleSmooth(states, loops, callback)) {
                this.pauseIdleIfFsPopupActive()
                return
            }

            this._animationCompleteCallback = null
            this.removeAnimationTrack()

            this._animation.stop()

            if (!states?.length) {
                if (callback) {
                    callback()
                }
                return
            }

            if (isDebugHighSymbol) {
                console.log('[H_ANIM_PLAY]', {
                    symbol: this.index === SymbolType.HP_1 ? 'H1' : 'H2',
                    index: this.index,
                    states,
                    loops,
                })
            }

            const firstTrack = this._animation.play(states[0], loops[0] ?? false)
            let lastTrack = firstTrack

            for (let index = 1; index < states.length; index++) {
                const queuedTrack = this._animation.queue(states[index], loops[index] ?? false)
                if (queuedTrack) {
                    lastTrack = queuedTrack
                }
            }

            this._animationTrack = lastTrack
            this.setAnimationPosition()

            if (this._animationTrack == null) {
                this.detachAnimation()
                if (callback) {
                    callback()
                }
                return
            }

            this.revealPreparedAnimationIfReady()

            this._animationCompleteCallback = callback
            this._animationTrack.listener = {
                complete: this.onCompleteAnimation,
                interrupt: this.onInterruptAnimation,
                end: this.onEndAnimation,
            }

            this.pauseIdleIfFsPopupActive()
        }
    }

    private pauseIdleIfFsPopupActive(): void {
        if (!(Facade as any).pauseHighPayIdleForFsPopup || !this.isHighPaySymbol(this.index)) {
            return
        }
        this.pauseIdleAnimation()
    }

    public isPlayingLoopingAnimation(nameFragment: string): boolean {
        const track = (this._animation as any)?.state?.tracks?.[0]
        if (!track || track.loop !== true) {
            return false
        }

        const currentName = String(track?.animation?.name || '').toLowerCase()
        return currentName.includes(String(nameFragment || '').toLowerCase())
    }

    public isPlayingAnimation(nameFragment: string): boolean {
        const track = (this._animation as any)?.state?.tracks?.[0]
        if (!track) {
            return false
        }

        const currentName = String(track?.animation?.name || '').toLowerCase()
        return currentName.includes(String(nameFragment || '').toLowerCase())
    }

    private getActiveAnimationTrack(): ITrackEntry | null {
        return ((this._animation as any)?.state?.tracks?.[0] as ITrackEntry | undefined) || this._animationTrack || null
    }

    public pauseIdleAnimation(): boolean {
        const track = this.getActiveAnimationTrack() as any
        const animationName = String(track?.animation?.name || '').toLowerCase()
        const isLooping = track?.loop === true || track?.loop === 1
        if (!track || !isLooping || !animationName.includes('idle')) {
            return false
        }

        track.timeScale = 0
        return true
    }

    public pauseWinAnimation(): boolean {
        const track = this.getActiveAnimationTrack() as any
        const animationName = String(track?.animation?.name || '').toLowerCase()
        const isLooping = track?.loop === true || track?.loop === 1
        if (!track || !isLooping || !animationName.includes('win')) {
            return false
        }

        track.timeScale = 0
        return true
    }

    public resumeIdleAnimation(): boolean {
        const track = this.getActiveAnimationTrack() as any
        const animationName = String(track?.animation?.name || '').toLowerCase()
        const isLooping = track?.loop === true || track?.loop === 1
        if (!track || !isLooping || !animationName.includes('idle')) {
            return false
        }

        track.timeScale = 1
        return true
    }

    /** Ensure spine is attached (pool out) without starting a clip. */
    public ensureAnimationAttached(): void {
        this.attachAnimation()
    }

    /**
     * Restart win from frame 0 in-place (no forceStatic / pool churn).
     * Call ensureAnimationAttached() on the whole line first, then this on each winner.
     */
    public restartWinLoopSynced(): void {
        this._animationCompleteCallback = null
        this.attachAnimation()

        if (this._animation == null) {
            return
        }

        this.removeAnimationTrack()

        try {
            this._animation.state.clearListeners()
        } catch {
            // ignore
        }

        try {
            this._animation.state.clearTracks()
        } catch {
            // ignore
        }

        // mixDuration 0 — hard cut, no blend lag between symbols
        const track = this._animation.play('win', true, 0)
        this._animationTrack = track
        this.setAnimationPosition()

        if (track) {
            track.trackTime = 0
            track.mixDuration = 0
            track.timeScale = 1
            track.listener = null
            this.revealPreparedAnimationIfReady()
        } else if (!this.isLowPayWinSymbol(this.index)) {
            this._animationWrapper.visible = true
            const keepStaticVisibleForHighPay =
                SymbolContainer.DEBUG_OVERLAY_STATIC_FOR_HIGH_PAY && this.isHighPaySymbol(this.index)
            this._staticImage.visible = keepStaticVisibleForHighPay
        }
    }

    /** Snap current win track back to t=0 (call after starting a whole line). */
    public syncWinTrackToStart(): void {
        const track = (this._animation as any)?.state?.tracks?.[0]
        if (!track) {
            return
        }

        // LP win clips are named a/k/q/j (no "win" in the name).
        if (!this.isPlayingAnimation('win') && !this.isLowPayWinSymbol(this.index)) {
            return
        }

        track.trackTime = 0
        track.mixTime = 0
        track.mixDuration = 0
        track.timeScale = 1
    }

    /** Dim a line-by-line leaver without detaching the spine (keeps rejoin instant/synced). */
    public freezeAsLineByLineLeaver(): void {
        this._animationCompleteCallback = null
        this.stopHpOutOverlayAnimation()

        if (this.isHighPaySymbol(this.index)) {
            this.freezeAtIdle()
            return
        }

        this.forceStatic()
    }

    private tryPlayHighPayWinIdleSmooth(
        states: string[],
        loops: boolean[],
        callback: (() => void) | null
    ): boolean {
        if (!this.isHighPaySymbol(this.index) || !this._animation) {
            return false
        }

        if (states.length !== 1 || (states[0] !== 'win' && states[0] !== 'idle')) {
            return false
        }

        const currentTrack = (this._animation as any)?.state?.tracks?.[0]
        if (!currentTrack) {
            return false
        }

        const targetState = states[0]
        const targetLoop = loops[0] ?? false
        const currentName = String(currentTrack?.animation?.name || '').toLowerCase()

        if (targetLoop && currentTrack.loop === true && currentName.includes(targetState)) {
            if (callback) {
                callback()
            }
            return true
        }

        this._animationCompleteCallback = null
        this.removeAnimationTrack()

        const mixDuration = targetState === 'idle' ? 0.2 : 0.12
        const firstTrack = this._animation.play(targetState, targetLoop, mixDuration)
        this._animationTrack = firstTrack
        this.setAnimationPosition()

        if (this._animationTrack == null) {
            this.detachAnimation()
            if (callback) {
                callback()
            }
            return true
        }

        this._animationCompleteCallback = callback
        this._animationTrack.listener = {
            complete: this.onCompleteAnimation,
            interrupt: this.onInterruptAnimation,
            end: this.onEndAnimation,
        }
        return true
    }

    private shouldQueueIdleAfterLand(states: string[], loops: boolean[]): boolean {
        if (!this.isHighPaySymbol(this.index)) {
            return false
        }

        if (states.length !== 1 || states[0] !== 'idle' || loops[0] !== true) {
            return false
        }

        const track = (this._animation as any)?.state?.tracks?.[0]

        if (!track || track.loop === true) {
            return false
        }

        const currentAnimationName = String(track?.animation?.name || '').toLowerCase()
        if (!currentAnimationName.includes('land')) {
            return false
        }

        const duration =
            (typeof track.animationEnd === 'number' && Number.isFinite(track.animationEnd) && track.animationEnd > 0
                ? track.animationEnd
                : track?.animation?.duration) || 0
        const trackTime =
            typeof track.trackTime === 'number' && Number.isFinite(track.trackTime) ? track.trackTime : 0

        if (duration <= 0) {
            return true
        }

        return trackTime < duration - 0.001
    }

    private shouldIgnoreRedundantIdleLoop(states: string[], loops: boolean[]): boolean {
        if (!this.isHighPaySymbol(this.index)) {
            return false
        }

        if (states.length !== 1 || states[0] !== 'idle' || loops[0] !== true) {
            return false
        }

        const track = (this._animation as any)?.state?.tracks?.[0]
        if (!track || track.loop !== true) {
            return false
        }

        const currentAnimationName = String(track?.animation?.name || '').toLowerCase()
        return currentAnimationName.includes('idle')
    }

    private shouldIgnoreRedundantLandIdleChain(states: string[], loops: boolean[]): boolean {
        if (!this.isHighPaySymbol(this.index)) {
            return false
        }

        if (states.length !== 2 || states[0] !== 'land' || states[1] !== 'idle') {
            return false
        }

        if (loops.length < 2 || loops[0] !== false || loops[1] !== true) {
            return false
        }

        const currentTrack = (this._animation as any)?.state?.tracks?.[0]
        if (!currentTrack || currentTrack.loop === true) {
            return false
        }

        const currentName = String(currentTrack?.animation?.name || '').toLowerCase()
        if (!currentName.includes('land')) {
            return false
        }

        const nextTrack = currentTrack?.next
        const nextName = String(nextTrack?.animation?.name || '').toLowerCase()

        return nextTrack?.loop === true && nextName.includes('idle')
    }

    private isHighPaySymbol(index: number): boolean {
        return (
            index === SymbolType.HP_1 ||
            index === SymbolType.HP_2 ||
            index === SymbolType.HP_3 ||
            index === SymbolType.HP_4
        )
    }

    private isLowPayWinSymbol(index: number): boolean {
        return (
            index === SymbolType.LP_1 ||
            index === SymbolType.LP_2 ||
            index === SymbolType.LP_3 ||
            index === SymbolType.LP_4
        )
    }

    private shouldRevealAnimationAfterFirstFrame(index: number): boolean {
        return this.isLowPayWinSymbol(index) || index === SymbolType.LP_5
    }

    private getMultiplierStaticTexture(multiplier: number, fallbackTextureName: string): PIXI.Texture {
        const normalized = SymbolContainer.AVAILABLE_MULTIPLIER_TEXTURES.includes(Number(multiplier))
            ? Number(multiplier)
            : 2
        const candidate = PIXI.Texture.from(`x${normalized}.png`)

        if (candidate && candidate !== PIXI.Texture.EMPTY) {
            return candidate
        }

        return PIXI.Texture.from(fallbackTextureName)
    }

    public stopAnimation() {
        const currentAnimation = this._animation

        if (this.animation) {
            this.animation.state.clearListeners() // TODO isn't beautiful
        }

        this.removeAnimationTrack()
        this.completeAnimation()

        // If callback started another animation on this symbol, do not detach to static between states.
        if (this._animation == null || this._animation !== currentAnimation) {
            return
        }

        if (this._animationTrack != null) {
            return
        }

        const hasRunningStateTrack = !!(this._animation as any)?.state?.tracks?.[0]
        if (hasRunningStateTrack) {
            return
        }

        this.detachAnimation()
    }

    /**
     * Hard-stop any looping spine and show the static texture.
     * Unlike stopAnimation(), this always detaches even when a track is still running
     * (e.g. Hold & Win collected coins that were left on a looping 'win').
     */
    public forceStatic(): void {
        this._animationCompleteCallback = null

        // LP shares `lv` (a/k/q/j): hide spine before clearTracks/stop, or setup-pose
        // flashes the wrong letter for a frame at end of the winline.
        this._animationWrapper.visible = false
        this._staticImage.visible = true

        if (this.animation) {
            try {
                this.animation.state.clearListeners()
            } catch {
                // ignore
            }
            try {
                this.animation.state.clearTracks()
            } catch {
                // ignore
            }
            try {
                this.animation.stop()
            } catch {
                // ignore
            }
        }

        this.removeAnimationTrack()

        if (this._animation != null) {
            this.detachAnimation()
        }
    }

    /**
     * Freeze the spine on the first idle frame (no detach).
     * Keeps coin size consistent with in-feature idle/win spines — prize static
     * textures are larger and look oversized after collect if we forceStatic().
     */
    public freezeAtIdle(): void {
        this._animationCompleteCallback = null
        this.attachAnimation()

        if (this._animation == null) {
            this.forceStatic()
            return
        }

        this.removeAnimationTrack()

        try {
            this._animation.state.clearListeners()
        } catch {
            // ignore
        }

        try {
            this._animation.stop()
        } catch {
            // ignore
        }

        this.setAnimationPosition()

        const track = this._animation.play('idle', false)
        this._animationTrack = track

        if (track) {
            track.trackTime = 0
            track.timeScale = 0
            track.listener = null
        }

        try {
            ;(this._animation as any).update?.(0)
        } catch {
            // ignore
        }

        this._animationWrapper.visible = true
        this._staticImage.visible = false

        // Re-apply tint after attach/play — pooled spines often reset light to full bright.
        this.tintPower = this._tintPower
    }

    public shadowIn(animate: boolean = true, power: number = -1) {
        gsap.killTweensOf(this, 'tintPower')

        if (power === -1) {
            power = SymbolContainer.SHADOW_IN_DEFAULT_TINT_POWER
        }

        if (animate) {
            gsap.to(this, { duration: 0.5, tintPower: power })
        } else {
            this.tintPower = power
        }
    }

    public shadowOut(animate: boolean = true) {
        gsap.killTweensOf(this, 'tintPower')

        if (animate) {
            gsap.to(this, { duration: 0.5, tintPower: 0 })
        } else {
            this.tintPower = 0
        }
    }

    protected attachAnimation() {
        if (this._animation == null) {
            this._animation = SymbolsAnimationPool.out(this._animationWrapper, this._symbolData)

            if (this._animation != null) {
                this._animation.state.listeners = []

                // Ensure pooled animations do not keep stale tint/light from previous symbol usages.
                const brightness = 1.0 - this._tintPower
                const channel = Math.max(0, Math.min(255, Math.round(brightness * 255)))
                const tintColor = (channel << 16) | (channel << 8) | channel
                this.applyAnimationTint(tintColor, brightness)

                // Keep static up until the first animation frame is applied. Besides the shared
                // LP skeleton, Wild needs this to avoid exposing its pooled setup pose.
                if (this.shouldRevealAnimationAfterFirstFrame(this.index)) {
                    this._animationWrapper.visible = false
                    this._staticImage.visible = true
                } else {
                    this._animationWrapper.visible = true
                    const keepStaticVisibleForHighPay =
                        SymbolContainer.DEBUG_OVERLAY_STATIC_FOR_HIGH_PAY && this.isHighPaySymbol(this.index)
                    this._staticImage.visible = keepStaticVisibleForHighPay
                }
            }
        }
    }

    private revealPreparedAnimationIfReady(): void {
        if (!this.shouldRevealAnimationAfterFirstFrame(this.index) || !this._animation || !this._animationTrack) {
            return
        }

        try {
            this._animation.state.apply(this._animation.skeleton)
            ;(this._animation as any).update?.(0)
        } catch {
            // Keep the static symbol visible until the next render if Spine is not ready yet.
            return
        }

        this._animationWrapper.visible = true
        this._staticImage.visible = false
    }

    protected setAnimationPosition() {
        const symbolAnimationScale = this.getSymbolAnimationScale()
        this._animation.position.set(0, 0)
        this._animation.scale.set(symbolAnimationScale)

        switch (this.index) {
            case SymbolType.LP_1:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(6, -4)
                break
            case SymbolType.LP_2:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -2)
                break
            case SymbolType.LP_3:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -3)
                break
            case SymbolType.LP_4:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -4)
                break
            case SymbolType.LP_5:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                this._animation.position.y = 10
                break
            case SymbolType.HP_4:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                this._animation.position.set(-1.5, 0)
                break
            case SymbolType.HP_3:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                this._animation.position.set(-1.5, 0)
                break
            case SymbolType.HP_2:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                this._animation.position.set(1, 0)
                break
            case SymbolType.HP_1:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
               // this._animation.position.set(-1.5, 0)
                break
            case SpecialSymbolType.SCATTER:
                //this._animation.position.set(-0.5, -5.5)
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                break
            case SpecialSymbolType.MULTIPLIER:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(-2, -5)
                break
        }
    }

    private getSymbolAnimationScale(): number {
        const animationScaleBoost = this.getAnimationScaleBoost()

        // HP + LP win spines: match the on-screen static sprite so win clips aren't oversized.
        if (
            this.isHighPaySymbol(this.index) ||
            this.isLowPayWinSymbol(this.index) ||
            this.index === SymbolType.LP_5
        ) {
            const staticScaleX = Math.abs(this._staticImage.scale.x)
            if (Number.isFinite(staticScaleX) && staticScaleX > 0) {
                return staticScaleX * animationScaleBoost
            }

            return SymbolContainer.HIGH_PAY_SYMBOL_SCALE_FALLBACK * animationScaleBoost
        }

        if (this._fixedAnimationScale != null) {
            return this._fixedAnimationScale * animationScaleBoost
        }

        const baseScale = SymbolContainer.USE_DYNAMIC_SYMBOL_ANIMATION_FIT
            ? this.getDynamicAnimationScale()
            : SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE

        return baseScale * animationScaleBoost
    }

    private getAnimationScaleBoost(): number {
        const textureName = String(this._staticTextureName || '').toLowerCase()

        if (this.index === SymbolType.LP_5 || textureName.includes('wild')) {
            return 1
        }

        if (this.index === SpecialSymbolType.SCATTER || textureName.includes('bonus')) {
            return SymbolContainer.BONUS_SYMBOL_ANIMATION_SCALE_BOOST
        }

        return 1
    }

    private getDynamicAnimationScale(): number {
        if (!this._animation) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        const staticTexture = this._staticImage.texture
        const staticTextureWidth = staticTexture?.orig?.width ?? staticTexture?.width ?? 0
        const staticTextureHeight = staticTexture?.orig?.height ?? staticTexture?.height ?? 0

        // Prefer matching the exact on-screen static symbol size to avoid idle oversizing.
        const targetWidth =
            staticTextureWidth > 0 ? staticTextureWidth * Math.abs(this._staticImage.scale.x) : Facade.symbolsConfig.WIDTH
        const targetHeight =
            staticTextureHeight > 0
                ? staticTextureHeight * Math.abs(this._staticImage.scale.y)
                : Facade.symbolsConfig.HEIGHT

        let boundsWidth = 0
        let boundsHeight = 0

        try {
            // Ensure transform/bounds are up to date before measuring
            this._animation.update(0)
            const bounds = this._animation.getLocalBounds()
            boundsWidth = bounds?.width ?? 0
            boundsHeight = bounds?.height ?? 0
        } catch (error) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        if (boundsWidth <= 0 || boundsHeight <= 0) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        const fitScale = Math.min(targetWidth / boundsWidth, targetHeight / boundsHeight)

        if (!Number.isFinite(fitScale) || fitScale <= 0) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        return fitScale
    }

    protected removeAnimationTrack() {
        if (this._animationTrack != null) {
            this._animationTrack.listener = null
            this._animationTrack = null
        }
    }

    protected detachAnimation() {
        if (this._animation != null) {
            const animation = this._animation

            this._animationCompleteCallback = null
            this.removeAnimationTrack()

            // Show static now — pool.in() resets the shared `lv` skeleton to setup-pose
            // (wrong letter) while the spine is still parented for one timeout tick.
            this._animationWrapper.visible = false
            this._staticImage.visible = true

            setTimeout(() => {
                // sprite position shifting, if called without timeout
                if ((animation as any)?.color?.setLight) {
                    ;(animation as any).color.setLight(1, 1, 1)
                }
                animation.tint = 0xffffff
                const animationRoot = animation as unknown as PIXI.Container
                if (animationRoot?.children?.length) {
                    for (const child of animationRoot.children) {
                        if (child && 'tint' in child) {
                            ;(child as PIXI.Container & { tint: number }).tint = 0xffffff
                        }
                    }
                }
                SymbolsAnimationPool.in(animation)
            })

            this._animation = null
        }
    }

    protected onCompleteAnimation = (track: ITrackEntry) => {
        if (this._animationTrack === track) {
            if (track.loop) {
                this.completeAnimation()
            } else {
                this.stopAnimation()
            }
        }

        // After a non-loop clip (e.g. land) the queued idle becomes current — freeze it if needed.
        if ((Facade as any).pauseHighPayIdleForFsPopup && this.isHighPaySymbol(this.index)) {
            this.pauseIdleAnimation()
        }
    }

    protected onInterruptAnimation = (track: ITrackEntry) => {
        if (this._animationTrack !== track) {
            return
        }

        // Interrupt means a new animation replaced the current one; do not force stop/detach here.
        this.removeAnimationTrack()
    }

    protected onEndAnimation = (track: ITrackEntry) => {
        if (this._animationTrack !== track) {
            return
        }

        // End can follow complete; avoid duplicate stop/detach.
    }

    protected completeAnimation() {
        if (this._animationCompleteCallback != null) {
            this._animationCompleteCallback()
            this._animationCompleteCallback = null
        }
    }

    public set tintPower(value: number) {
        // Always re-apply child tints even when the value is unchanged.
        // Pooled spines / BitmapText can hold a stale tint after recycle, which left
        // Gold Coins looking dimmed during anticipation while the amount stayed bright.
        const brightness = 1.0 - value
        const channel = Math.max(0, Math.min(255, Math.round(brightness * 255)))
        const tintColor = (channel << 16) | (channel << 8) | channel

        this._staticImage.tint = tintColor
        this._multiplierValueImage.tint = tintColor
        this._multiplierValue.content.tint = tintColor
        this._animationMultiplierValue.content.tint = tintColor
        this._tintPower = value

        this.applyAnimationTint(tintColor, brightness)
    }

    private applyAnimationTint(tintColor: number, brightness: number = 1): void {
        if (this._animation == null) {
            return
        }

        // Prefer Spine setLight when available. Applying setLight AND PIXI tint multiplies
        // (e.g. FS collect focus 0.82 → ~3% effective brightness = looks like double dim).
        if ((this._animation as any)?.color?.setLight) {
            ;(this._animation as any).color.setLight(brightness, brightness, brightness)
            this._animation.tint = 0xffffff

            const animationRoot = this._animation as unknown as PIXI.Container
            if (animationRoot?.children?.length) {
                for (const child of animationRoot.children) {
                    if (child && 'tint' in child) {
                        ;(child as PIXI.Container & { tint: number }).tint = 0xffffff
                    }
                }
            }
            return
        }

        this._animation.tint = tintColor

        // spine-pixi-v8 meshes can keep a previous container tint; force children too.
        const animationRoot = this._animation as unknown as PIXI.Container
        if (animationRoot?.children?.length) {
            for (const child of animationRoot.children) {
                if (child && 'tint' in child) {
                    ;(child as PIXI.Container & { tint: number }).tint = tintColor
                }
            }
        }
    }

    public get tintPower(): number {
        return this._tintPower
    }

    public get index(): number {
        return this._symbolData.index
    }

    public get data() {
        return this._symbolData
    }

    public get animation() {
        return this._animation
    }

    public showMultiplierLandAnimation = (onComplete?: () => void) => {
        const restoreMultiplierStaticValueVisibility = this._multiplierValueImage.visible
        this._multiplierValueImage.visible = false

        let finished = false
        let fallbackTween: gsap.core.Tween | null = null

        const finish = () => {
            if (finished) {
                return
            }

            finished = true

            if (fallbackTween) {
                fallbackTween.kill()
                fallbackTween = null
            }

            this._multiplierValueImage.visible = restoreMultiplierStaticValueVisibility

            if (onComplete) {
                onComplete()
            }
        }

        this.playAnimation('land', false, finish, true)

        // Fallback: alcuni asset/stati spine possono non emettere complete in modo affidabile.
        fallbackTween = gsap.delayedCall(0.9, finish)

        if (this._animation?.replaceInSlotContainer) {
            this._animation.replaceInSlotContainer('number', this._animationMultiplierValue)
        }
    }

    public playLandingLoopOverlayOnStatic = () => {
        this.attachAnimation()

        if (!this._animation) {
            return
        }

        this._animationCompleteCallback = null
        this.removeAnimationTrack()
        this._animation.stop()

        this._animationTrack = this._animation.play('land', true)
        this.setAnimationPosition()

        // Overlay mode for test: keep static visible and play landing loop above it.
        this._staticImage.visible = true
        this._animationWrapper.visible = true
    }

    public getCurrentTrackDuration = (): number => {
        const track = (this._animation as any)?.state?.tracks?.[0]
        const duration = track?.animation?.duration

        if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
            return duration
        }

        return 0
    }
}
